import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useBlocker, useLocation } from "react-router-dom";
import ClosePageWarningModal from "./ClosePageWarningModal";
import ConfirmationModal from "./ConfirmationModal";

/**
 * "Discard your changes?" for edit pages (AKL-1083).
 *
 * One provider for the whole app, because React Router allows one blocker at a time and two
 * edit pages can be mounted together: a deployment's page renders inside its project's page,
 * and the project form keeps its edits while the user is on another of the project's tabs.
 * Each page registers whether it holds unsaved edits and the part of the app they belong to
 * (its "scope", a path prefix). The provider then asks once, whichever page's edits a
 * navigation would lose:
 *
 * - **In-app navigation** (a link, `navigate()`, the browser's Back and Forward) is held by
 *   `useBlocker` when it leaves the scope of any page with unsaved edits. Staying inside the
 *   scope — another tab of the same project — loses nothing, so it is let through.
 * - **Leaving the site** (reload, closing the tab, a typed URL) gets the browser's own
 *   leave-page warning through `ClosePageWarningModal`. Browsers show their own wording there;
 *   a page cannot replace it.
 * - **Cancel and Close** buttons call `confirmLeave`, which asks whenever this page has
 *   unsaved edits, even when the place Cancel goes back to is inside the scope and would keep
 *   them.
 *
 * `useBlocker` needs a data router, which is why `AuthGate` mounts the app through
 * `createBrowserRouter`. Without this provider (a page rendered on its own in a unit test)
 * the hook asks nothing and leaves at once.
 */

type Registration = {
  dirty: boolean;
  scope: string;
};

type UnsavedChangesContextValue = {
  register: (id: string, registration: Registration) => void;
  unregister: (id: string) => void;
  /** Ask, then run `leave` if the user chooses to discard. */
  ask: (leave: () => void) => void;
  /** Run `leave` and let the navigation it starts through unasked. */
  leaveWithoutAsking: (leave: () => void) => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
  null,
);

/**
 * How long a `leaveWithoutAsking` pass stays open if the navigation it was for never reports
 * a new location (`navigate(-1)` with nothing to go back to). Long enough for a Back
 * navigation's `popstate` to arrive, far too short to make and lose a new edit in.
 */
const PASS_TIMEOUT_MS = 2000;

/**
 * Whether `pathname` is `scope` or below it.
 * @param pathname the path a navigation is going to
 * @param scope the path prefix a page's edits belong to
 * @returns true when the navigation stays where the edits live
 */
export function isInsideScope(pathname: string, scope: string): boolean {
  const base = scope.endsWith("/") ? scope.slice(0, -1) : scope;
  return pathname === base || pathname.startsWith(base + "/");
}

/**
 * A copy of a form value that compares equal whenever the difference would not be an edit:
 * blank text and a missing value are the same, as are a field typed into and cleared again,
 * and key order does not matter. An object left with no values is blank as well, so a nested
 * field typed into and cleared (a template field in `schema_data`) is no edit either
 * (Copilot, PR #847 round 6). Arrays keep their order.
 * @param value a form state, or any part of one
 * @returns the canonical copy
 */
function canonical(value: unknown): unknown {
  if (value === "" || value === undefined || value === null) return null;
  if (Array.isArray(value)) return value.map(canonical);
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as object).sort()) {
      const inner = canonical((value as Record<string, unknown>)[key]);
      if (inner !== null) out[key] = inner;
    }
    return Object.keys(out).length === 0 ? null : out;
  }
  return value;
}

/**
 * Whether a form's current state holds an edit its saved state does not.
 * @param current the form as it is now
 * @param saved the form as last loaded or saved
 * @returns true when the two differ by more than blanks, missing keys and key order
 */
export function formsDiffer(current: unknown, saved: unknown): boolean {
  return (
    JSON.stringify(canonical(current)) !== JSON.stringify(canonical(saved))
  );
}

/**
 * The single blocker, leave-page warning and confirmation for every edit page. Mount it once,
 * inside the data router.
 * @param props.children the app
 * @returns the app, with the confirmation when it is needed
 */
export const UnsavedChangesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const registrations = useRef(new Map<string, Registration>());
  // Mirrors "any registration is dirty" for the leave-page warning, which renders from state.
  const [anyDirty, setAnyDirty] = useState(false);
  const pass = useRef(false);
  const passTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);

  const refreshAnyDirty = useCallback(() => {
    setAnyDirty(
      Array.from(registrations.current.values()).some((r) => r.dirty),
    );
  }, []);

  const register = useCallback(
    (id: string, registration: Registration) => {
      registrations.current.set(id, registration);
      refreshAnyDirty();
    },
    [refreshAnyDirty],
  );

  const unregister = useCallback(
    (id: string) => {
      registrations.current.delete(id);
      refreshAnyDirty();
    },
    [refreshAnyDirty],
  );

  const closePass = useCallback(() => {
    pass.current = false;
    if (passTimer.current) {
      clearTimeout(passTimer.current);
      passTimer.current = null;
    }
  }, []);

  const leaveWithoutAsking = useCallback(
    (leave: () => void) => {
      closePass();
      pass.current = true;
      passTimer.current = setTimeout(closePass, PASS_TIMEOUT_MS);
      leave();
    },
    [closePass],
  );

  // A navigation that landed ends the pass it was given, so the next one is asked about.
  useEffect(() => {
    closePass();
  }, [location.key, closePass]);

  useEffect(() => closePass, [closePass]);

  // Reads refs, not state: a page that saves and navigates in one handler has not re-rendered
  // yet when the router asks, and neither has this provider.
  const shouldBlock = useCallback(
    ({ nextLocation }: { nextLocation: { pathname: string } }) => {
      if (pass.current) return false;
      return Array.from(registrations.current.values()).some(
        (r) => r.dirty && !isInsideScope(nextLocation.pathname, r.scope),
      );
    },
    [],
  );
  const blocker = useBlocker(shouldBlock);

  const ask = useCallback((leave: () => void) => {
    // Wrapped: a function passed to a state setter would be called as an updater.
    setPendingLeave(() => leave);
  }, []);

  const value = useMemo(
    () => ({ register, unregister, ask, leaveWithoutAsking }),
    [register, unregister, ask, leaveWithoutAsking],
  );

  const blocked = blocker.state === "blocked";
  const open = blocked || pendingLeave !== null;

  const keepEditing = () => {
    if (blocked) blocker.reset?.();
    setPendingLeave(null);
  };

  // Clears a pending Cancel first on every path: Back pressed while the Cancel question is
  // open blocks too, and proceeding with that navigation must not leave the Cancel waiting
  // to run at the destination (Copilot, PR #847 round 4).
  const discard = () => {
    const leave = pendingLeave;
    setPendingLeave(null);
    if (blocked) {
      blocker.proceed?.();
      return;
    }
    if (leave) leaveWithoutAsking(leave);
  };

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <ClosePageWarningModal
        warnBeforeClosing={anyDirty}
        warningMessage={t(
          "You have changes you have not saved. If you leave now, they are lost.",
        )}
      />
      <ConfirmationModal
        isOpen={open}
        title={t("Discard your changes?")}
        confirmationMessage={t(
          "You have changes you have not saved. If you leave now, they are lost.",
        )}
        confirmLabel={t("Discard changes")}
        cancelLabel={t("Keep editing")}
        onConfirm={discard}
        onCancel={keepEditing}
        danger
      />
    </UnsavedChangesContext.Provider>
  );
};

/**
 * Register a form's unsaved edits with the app's guard.
 *
 * Without an `UnsavedChangesProvider` above it (a page rendered alone in a unit test) nothing
 * is registered, `confirmLeave` leaves at once and `leaveWithoutAsking` just leaves.
 * @param dirty whether the form holds edits it has not saved
 * @param scope the path prefix the edits belong to: navigating within it keeps them
 * @returns `confirmLeave` for Cancel and Close, and `leaveWithoutAsking` for the navigation
 *   that follows a successful save. That pass lets the next navigation through for every
 *   page's edits, not only this page's, so use it only for a navigation no other mounted
 *   page's edits would block. Today's callers qualify: a project's create and migration have
 *   no other page mounted, and a deployment's Save goes to its project's deployments list,
 *   inside the project's scope (Copilot, PR #847 round 8).
 */
export function useUnsavedChanges(
  dirty: boolean,
  scope: string,
): {
  confirmLeave: (leave: () => void) => void;
  leaveWithoutAsking: (leave: () => void) => void;
} {
  const context = useContext(UnsavedChangesContext);
  const id = useId();
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Layout effects, not passive ones: they run before the browser handles the next event,
  // so a click on a button the render has just enabled (Close, after a save) never meets a
  // registration from the render before it.
  useLayoutEffect(() => {
    context?.register(id, { dirty, scope });
  }, [context, id, dirty, scope]);

  useLayoutEffect(() => {
    return () => context?.unregister(id);
  }, [context, id]);

  const confirmLeave = useCallback(
    (leave: () => void) => {
      if (!context) {
        leave();
        return;
      }
      // Asked on this page's own edits; the router still guards any other page's.
      if (dirtyRef.current) {
        context.ask(leave);
      } else {
        leave();
      }
    },
    [context],
  );

  const leaveWithoutAsking = useCallback(
    (leave: () => void) => {
      if (!context) {
        leave();
        return;
      }
      context.leaveWithoutAsking(leave);
    },
    [context],
  );

  return { confirmLeave, leaveWithoutAsking };
}
